import { scaleTime, scaleLinear } from "d3-scale"
import { axisBottom } from "d3-axis"
import { createSelector } from "reselect"
import moment from "moment"
import { first, last } from "lodash"
import { getTotalData } from "./data"
import { calculateHighlighter } from "../../helpers/highlighter"
import { xAxisTicks } from "../../helpers/x-axis-ticks"
import { getTotalItem } from "./items"

export const MARGINS = { top: 15, left: 65, right: 40, bottom: 15 }
export const getHeight = props => props.height
export const getWidth = props => props.width || 150
export const getTickFormat = props => props.tickFormat
const getDate = props => props.date
const getStartDate = props => props.startDate
const getEndDate = props => props.endDate
const getIsPeriodIntraday = props => props.isPeriodIntraday

const getMinX = createSelector(
  [getStartDate, getTotalData],
  (startDate, data) => {
    if (data && data.length > 0) return first(data).date.toDate()
    return moment(startDate).toDate()
  },
)

const getMaxX = createSelector(
  [getEndDate, getTotalData],
  (endDate, data) => {
    if (data && data.length > 0) return last(data).date.toDate()
    return moment(endDate).toDate()
  },
)

const getXTicks = createSelector(
  [getMinX, getMaxX],
  xAxisTicks,
)

export const getScaleX = createSelector(
  [getMinX, getMaxX, getWidth],
  (minX, maxX, width) => scaleTime()
    .domain([minX, maxX])
    .rangeRound([MARGINS.left, width - MARGINS.right]),
)

const getTotalMinY = createSelector(
  [getTotalData, getTotalItem],
  (data, items) => data.reduce((min, datum) => {
    return Math.min(min, ...items.map(({ id }) => datum[id]).filter(datum => datum))
  }, Infinity) - 0.01,
)

const getTotalMaxY = createSelector(
  [getTotalData, getTotalItem],
  (data, items) => data.reduce((max, datum) => {
    return Math.max(max, ...items.map(({ id }) => datum[id]).filter(datum => datum))
  }, -Infinity) + 0.01,
)

export const getTotalScaleY = createSelector(
  [getTotalMinY, getTotalMaxY, getHeight],
  (minY, maxY, height) => {
    return scaleLinear()
      .domain([minY, maxY])
      .rangeRound([height - MARGINS.bottom, MARGINS.top])
  },
)

export const getXAxis = createSelector(
  [getHeight, getWidth, getScaleX, getXTicks],
  (height, width, scaleX, [ticks, formatter]) => ({
    fn: axisBottom()
      .scale(scaleX)
      .ticks(ticks)
      .tickSize(-height + MARGINS.top + MARGINS.bottom)
      .tickFormat(formatter),
    transform: `translate(0,${height - MARGINS.bottom})`,
    baseline: {
      x1: MARGINS.left,
      x2: width - MARGINS.right,
      y1: height - MARGINS.bottom,
      y2: height - MARGINS.bottom,
    },
  }),
)

export const getHighlighter = createSelector(
  [getDate, getScaleX, getTotalScaleY, getMinX, getMaxX, getTotalMinY, getTotalMaxY, getIsPeriodIntraday],
  // The selected-date highlighter is a multi-day affordance; intraday has no date selection to highlight.
  (date, scaleX, scaleY, minX, maxX, minY, maxY, isPeriodIntraday) =>
    isPeriodIntraday ? null : calculateHighlighter(date, scaleX, scaleY, minX, maxX, minY, maxY),
)
